import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, Users, Award, CalendarClock, Server, Clock } from "lucide-react";

const reasons = [
  {
    icon: ShieldCheck,
    stat: "PE",
    label: "Certified Engineers",
    body: "Every connection package is reviewed and stamped by US-certified structural PEs before it leaves our shop.",
  },
  {
    icon: Zap,
    stat: "48h",
    label: "Turnaround",
    body: "Priority queues for time-critical erection sequences — from RFI to revised drawings in as little as two business days.",
  },
  {
    icon: Users,
    stat: "850+",
    label: "Projects Delivered",
    body: "Trusted by GCs, EPC firms and fabricators across the US on commercial, industrial and infrastructure builds.",
  },
  {
    icon: Award,
    stat: "AISC",
    label: "Code-Fluent",
    body: "Deep working knowledge of AISC 360, AWS D1.1, IBC and OSHA erection standards — no re-work at the mill.",
  },
];

const commitments = [
  {
    icon: CalendarClock,
    label: "Rigorous Schedule",
    body: "Milestone-driven delivery with weekly progress reports, submittal tracking and IFC-lock dates you can bank on.",
  },
  {
    icon: Server,
    label: "FTP Server Access",
    body: "Dedicated FTP workspace per project — instant file exchange, revision-controlled drops and audit-ready logs 24/7.",
  },
  {
    icon: Clock,
    label: "3 Working Shifts",
    body: "Round-the-clock detailing across three shifts means your job keeps moving even while your US office sleeps.",
  },
];

export const WhyUs = () => (
  <section
    id="why-us"
    className="relative py-20 md:py-28 border-t border-slate-200/60 bg-slate-50/60 overflow-hidden"
  >
    <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-slate-200/60 rounded-full blur-3xl pointer-events-none" />

    <div className="relative px-6 md:px-10 lg:px-14 xl:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-20">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
            <span className="w-8 h-px bg-[#0A2540]" />
            06 · Why us
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.1]">
            The detail is
            <br />
            in the <span className="text-slate-400">details</span>.
          </h2>
        </div>
        <div className="lg:col-span-5 flex lg:items-end">
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            You don&apos;t need another vendor — you need a detailing partner
            that thinks like the fabricator, moves at the pace of the site, and
            gets it right the first time.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {reasons.map((r, i) => {
          const Icon = r.icon;
          return (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`why-us-card-${i}`}
              className="group relative bg-white border border-slate-200 rounded-2xl p-6 md:p-7 hover:border-[#0A2540]/60 hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#0A2540] flex items-center justify-center group-hover:bg-[#0A2540] transition-colors">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div className="font-display text-3xl font-bold text-[#0A2540] leading-none">
                  {r.stat}
                </div>
              </div>

              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0A2540] mb-2">
                {r.label}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{r.body}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Our commitments */}
      <div className="mt-14 md:mt-20">
        <div className="flex items-center gap-3 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
          <span className="w-8 h-px bg-[#0A2540]" />
          Our commitments
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {commitments.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`commitment-card-${i}`}
                className="group relative bg-[#0A2540] text-white rounded-2xl p-6 md:p-7 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#0A2540]/10 rounded-full blur-2xl pointer-events-none" />
                <div className="relative flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center group-hover:bg-[#0A2540] group-hover:border-transparent transition-colors">
                    <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-lg">
                      {c.label}
                    </div>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);
